import _ from 'lodash';
import identity from 'lodash/identity';
import isFunction from 'lodash/isFunction';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import { call, delay, fork, put, race, select, spawn, take } from 'redux-saga/effects';

// see https://github.com/acdlite/redux-actions/blob/master/src/createAction.js
export function createAction(type, payloadCreator = identity, metaCreator) {
  const finalPayloadCreator = isNull(payloadCreator) ? identity : payloadCreator;

  const actionCreator = (...args) => {
    const hasError = args[0] instanceof Error;

    const action = {
      type,
    };

    const payload = hasError ? args[0] : finalPayloadCreator(...args);
    if (!isUndefined(payload)) {
      action.payload = payload;
    }

    if (hasError || payload instanceof Error) {
      // Handle FSA errors where the payload is an Error object. Set error.
      action.error = true;
    }

    if (isFunction(metaCreator)) {
      action.meta = metaCreator(...args);
    }

    return action;
  };

  actionCreator.toString = () => type.toString();

  return actionCreator;
}

export function createReducer(defaultState, typeMappings = {}) {
  // if provided, use default state function; if not, default to returning state
  let defaultAction = typeMappings.default ? typeMappings.default : (state, action) => state;

  return function (state = defaultState, action) {
    if (!action || !action.type) return defaultAction(state, action);
    if (typeMappings[action.type]) {
      return typeMappings[action.type](state, action);
    }
    return defaultAction(state, action);
  };
}

/**
 * Returns a function that performs a set of really simple state changes, without regard for the action payload.
 * @param changes
 * @returns {Function}
 */
export function simpleStateChange(changes = {}) {
  return function (state) {
    return state.merge(changes);
  };
}

/**
 * Takes ANY pattern defined in the object, and returns its results
 * @param patternObject
 * @param action
 */
export function* takeAll(patternObject) {
  let output = {};
  let patterns = _.cloneDeep(patternObject);
  while (true) {
    const result = yield race(patterns);
    const key = Object.keys(result)[0];
    output[key] = result[key];
    _.unset(patterns, key);
    if (_.size(patterns) === 0) break;
  }
  return output;
}

/**
 * Returns a function that dispatches an action using the given action creator. It passes the payload entirely.
 * @param actionCreator
 * @param payloadKeys
 */
export function putAction(actionCreator, ...payloadKeys) {
  if (!actionCreator) {
    // console.warn('WARNING: invalid action creator specified in UISagas.')
  }
  return function* (payload) {
    const args = _.map(payloadKeys, (k) => payload[k]);
    yield put(actionCreator(...args));
  };
}

/**
 * Returns a function that grabs the payload from an action and calls whatever function you provide with it
 * @param fn
 * @returns {Function}
 */
export function callWithPayload(fn) {
  return function* (action) {
    yield call(fn, action.payload);
  };
}

/**
 * Returns a function that grabs the payload from an action and forks whatever function you provide with it
 * @param fn
 * @returns {Function}
 */
export function forkWithPayload(fn) {
  return function* (action) {
    yield fork(fn, action.payload);
  };
}

/**
 * Returns a function that grabs the payload from an action and spawns whatever function you provide with it
 * @param fn
 * @returns {Function}
 */
export function spawnWithPayload(fn) {
  return function* (action) {
    yield spawn(fn, action.payload);
  };
}

/**
 * Waits for a piece of state to meet a certain condition, selects that state, and returns it.
 * @param selector The selector function to grab the state we want to check
 * @param condition Condition to meet. Defaults to 'selector is truthy'
 * @param options
 */
export function* selectWithWait(selector, condition = (a) => !!a, options = { interval: 1e3, timeout: 0 }) {
  const {
    interval,
    // not yet necessary but leaving this in as a potential option in the future
    // timeout
  } = options;
  let data = yield select(selector);
  while (!condition(data)) {
    if (interval > 0) {
      // if an interval is provided, check every second
      yield delay(interval);
    } else {
      // if no interval is provided, check every time an action is fired
      yield take();
    }
    data = yield select(selector);
  }
  return data;
}

/**
 * like takeLatest, but takes multiple actions and waits for ALL the actions to have appeared.
 * @param actionTypes
 * @param saga
 * @param once set to true if you only want this combination to run ONCE.
 */
export function* combineLatest(actionTypes, saga, once = false) {
  let actions = {};
  let done = false;
  while (once ? !done : true) {
    const action = yield take(actionTypes);
    actions[action.type] = action;

    if (allActionsReady(actions, actionTypes)) {
      done = true;
      yield saga(actionTypes.map((t) => actions[t])); // eslint-disable-line no-loop-func
      // clear actions after use
      actions = {};
    }
  }
}

function allActionsReady(actions, actionTypes) {
  return Object.keys(actions).length === actionTypes.length;
}
